import React from 'react';
import { useLocation } from 'react-router-dom';
import type { ViewCategory, NavItem } from '../config/navConfig';
import { NavViewItem } from './NavViewItem';

export interface NavViewGroupProps {
  view: ViewCategory;
  items: NavItem[];
  isCollapsed: boolean;
  onToggle: () => void;
  onItemClick?: () => void;
}

/**
 * Counts the number of distinct sub-groups in an items list (for height calculation).
 */
function subGroupCount(items: NavItem[]): number {
  const groups = new Set(items.map((i) => i.subGroup).filter(Boolean));
  return groups.size;
}

/**
 * Renders nav items with sub-group divider labels inserted between groups.
 */
function renderItemsWithSubGroups(
  items: NavItem[],
  currentPath: string,
  onItemClick?: () => void
): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let lastSubGroup: string | undefined;

  for (const item of items) {
    if (item.subGroup && item.subGroup !== lastSubGroup) {
      elements.push(
        <div key={`subgroup-${item.subGroup}`} style={styles.subGroupLabel}>
          {item.subGroup}
        </div>
      );
      lastSubGroup = item.subGroup;
    }
    elements.push(
      <NavViewItem
        key={item.path}
        item={item}
        isActive={currentPath === item.path}
        onClick={onItemClick}
      />
    );
  }

  return elements;
}

/**
 * Renders a collapsible view category with a header and child navigation items.
 * Handles expand/collapse animation and accessibility attributes.
 */
export function NavViewGroup({ view, items, isCollapsed, onToggle, onItemClick }: NavViewGroupProps) {
  const location = useLocation();
  const panelId = `nav-view-panel-${view.id}`;

  return (
    <div style={styles.group}>
      {/* Category Header */}
      <button
        style={styles.header}
        onClick={onToggle}
        aria-expanded={!isCollapsed}
        aria-controls={panelId}
      >
        <span style={styles.headerIcon}>{view.icon}</span>
        <span style={styles.headerLabel}>{view.label}</span>
        {isCollapsed && (
          <span style={styles.countBadge}>{items.length}</span>
        )}
        <span
          style={{
            ...styles.chevron,
            transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
          }}
        >
          ▾
        </span>
      </button>

      {/* Item List Panel */}
      <div
        id={panelId}
        role="region"
        style={{
          ...styles.panel,
          maxHeight: isCollapsed ? '0px' : `${items.length * 48 + subGroupCount(items) * 24 + 8}px`,
          opacity: isCollapsed ? 0 : 1,
        }}
      >
        {renderItemsWithSubGroups(items, location.pathname, onItemClick)}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  group: {
    marginBottom: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '8px 12px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    color: '#6b8572',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  headerIcon: {
    fontSize: '14px',
    width: '20px',
    textAlign: 'center' as const,
  },
  headerLabel: {
    flex: 1,
    textAlign: 'left' as const,
  },
  countBadge: {
    backgroundColor: 'rgba(163,184,169,0.2)',
    color: '#a3b8a9',
    fontSize: '10px',
    fontWeight: 700,
    padding: '1px 6px',
    borderRadius: '8px',
    minWidth: '18px',
    textAlign: 'center' as const,
  },
  chevron: {
    fontSize: '12px',
    transition: 'transform 0.2s ease',
    color: '#6b8572',
  },
  panel: {
    overflow: 'hidden',
    transition: 'max-height 0.25s ease, opacity 0.2s ease',
    paddingLeft: '4px',
  },
  subGroupLabel: {
    fontSize: '10px',
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.8px',
    color: '#a3d4b5',
    padding: '10px 12px 3px 36px',
  },
};
